// The League front page (PLAN-2026-10-17 §4, B-403, scenario K7's designed
// half): "What's happening in my league?" — catching up, as a front page
// does. One story at the top, then the table and the week's games, then the
// moves, the awards, what comes next, the archive and the office.
//
// THE SECTIONS ARE THE SCREENS. Each is a titled panel (U10) whose head has
// **Expand**, which grows it into its whole screen at its own address
// (`/standings`, `/awards`…) — the panel morphing into the page — and the
// top bar's Back or the page's own Collapse returns to the front page at its
// scroll. There are no doors: the page of doors this replaced answered
// "where can I go?", which the rail already answered, and went unused.
//
// ARRANGING IT STILL WORKS (L3, B-247): the account's order and hidden list
// are ids, and the sections' ids are new, so a page arranged before keeps
// what it can and puts the rest in their usual places.
//
// SINCE YOU LAST LOOKED LEADS THE PAGE (K7, B-411) while it has anything to
// say: my results, the league's moves and my players on the injury report
// since the front page was last shown to me, and chat's unread; **Got it**
// puts it away for the visit.
//
// THE BRACKET MOVES UP IN THE PLAYOFFS (B-408): once the regular season is
// over, *Schedule and playoffs* is *Playoffs*, the round under way from the
// bracket, second after the week's story on a page in its usual order. A
// page somebody arranged keeps their order.

import { useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  racesOnPoints,
  valueOf,
  failureOf,
  type BracketGame,
  type CatchUpView,
  type ChallengeBoardViewShape,
  type ChallengeWeekViewShape,
  type Snapshot,
  type BracketView,
  type HomeView,
  type SeasonWrapView,
  type StandingsView,
  type YearbookView,
} from '@fsp/domain';
import { useAccountPrefs } from '../../data/accountPrefs';
import { useAtLeast } from '../../data/detail';
import {
  deviceZone,
  useAwards,
  useBracket,
  useLeagueBase,
  useNews,
  useReaderZone,
  useRecap,
  useScoreboard,
  useSeasonWrap,
  useStandings,
  useWorthALook,
  useYearbook,
} from '../../data/hooks';
import { useLeagueSource } from '../../data/leagueSource';
import { isDemoLeague } from '../../data/demo';
import { exportLeague } from '../../data/exportFile';
import { usePickem } from '../../data/pickem';
import { fmtWhen } from '../shared/RosterMoveSheet';
import { useSession } from '../../data/session';
import { useInstallOffer } from '../../install/InstallOffer';
import { TeamMark } from '../../kit';
import { TeamPhoto } from './TeamPhoto';
import { HatPanel } from './HatPanel';
import { useLeagueHat } from '../../data/hat';
import { FrontNextSeason } from './NextSeason';
import { Panel } from '../../kit/Panel';
import { Button, Caption, Line, Meter, Stack, Text, tell } from '../../kit/ui';
import { Term } from '../../kit/Why';
import { morph } from '../../shell/morph';
import { DashboardScreen, type Widget } from '../../templates/DashboardScreen';
import { NewsRow, whenLabel } from '../NewsScreen';
import { hasNews, useCatchUp } from '../../data/catchUp';
import { useChatDrawer } from '../../shell/chatDrawer';
import { BracketColumn } from '../ScheduleScreen';
import { GameGrid } from '../scoreboard/GameGrid';
import { GameTile } from '../scoreboard/GameTile';
import { CardRow } from '../../kit/cards';
import { useTier } from '../../shell/useTier';
import { AwardsWidget } from '../shared/AwardsWidget';
import { PickemInfo, lockToName } from '../shared/PickemHow';
import { ChallengeInfo } from '../shared/ChallengeHow';
import { useChallengeBoard, useChallengeWeek } from '../../data/challenge';
import { Medal, ordinal, plural, slotsOf, tenths } from '../ChallengeScreen';
import { ChallengeBanner, ChallengePips } from '../shared/ChallengeBanner';
import { WorthInfo } from '../shared/WorthHow';
import { FrontWorth } from './FrontWorth';
import { GLOSSARY } from '../shared/glossary';
import { ArrangeLeaguePage, LeaguePageFoot } from '../shared/LeaguePageArrange';
import { arrangementOf, inOrder, isUsual } from '../shared/leaguePageLayout';
import { TeamName } from '../shared/Names';
import { RaceMark } from '../standings/StandingsSide';
import { fieldIsTheTop } from '../StandingsScreen';
import '../../styles/screens/league-front.css';
import { RosterMoveSheet, type RosterMove } from '../shared/RosterMoveSheet';
import { useTools } from '../../data/tools';

/** The section the person last expanded, so its panel is the one the page grows back from. */
let expanded: string | null = null;

/**
 * A section that grows into its own screen: its Expand, handed to whatever
 * draws the panel (a Panel here, the awards' own widget), and the box whose
 * panel morphs.
 */
function Expandable({
  id,
  to,
  label,
  children,
}: {
  id: string;
  to: string;
  /** What Expand grows into, for its name: "Expand the standings". */
  label: string;
  children: (expand: ReactNode) => ReactNode;
}) {
  const navigate = useNavigate();
  const box = useRef<HTMLDivElement>(null);
  const expand = (
    <Button
      size="xs"
      variant="quiet"
      icon="grow"
      label={`Expand ${label}`}
      onClick={() => {
        expanded = id;
        for (const el of document.querySelectorAll<HTMLElement>('[data-morph]'))
          delete el.dataset.morph;
        if (box.current !== null) box.current.dataset.morph = '';
        morph(() => navigate(to));
      }}
    >
      Expand
    </Button>
  );
  return (
    <div
      ref={box}
      className="fsp-front-section"
      // ONE NAME AT A TIME (the View Transitions API wants each unique): the
      // panel being expanded, or the one the page came back from.
      data-morph={expanded === id ? '' : undefined}
    >
      {children(expand)}
    </div>
  );
}

/** A section's panel: its title, and Expand to its whole screen where it has one. */
function Section({
  id,
  title,
  to,
  label,
  help,
  children,
}: {
  id: string;
  title: string;
  /** Its own screen's address; none for a section that is only links. */
  to?: string;
  label?: string;
  /** An info button beside the title, saying how the section plays (pick'em, B-484). */
  help?: ReactNode;
  children: ReactNode;
}) {
  if (to === undefined)
    return (
      <div className="fsp-front-section">
        <Panel title={title} help={help}>
          {children}
        </Panel>
      </div>
    );
  return (
    <Expandable id={id} to={to} label={label ?? title}>
      {(expand) => (
        <Panel title={title} meta={expand} help={help}>
          {children}
        </Panel>
      )}
    </Expandable>
  );
}

export function LeagueFront({ home }: { home: HomeView }) {
  const base = useLeagueBase();
  const zone = useReaderZone();
  const standard = useAtLeast('standard');
  const phone = useTier() === 'phone';
  const standingsRead = useStandings();
  const standings = valueOf(standingsRead);
  // THE WEEKLY HAT (PLAN-2026-10-30-the-weekly-hat §7), once a week has put one on.
  const hat = useLeagueHat();
  const pickem = usePickem(undefined);
  const worth = useWorthALook();
  const tools = useTools();
  const boardRead = useScoreboard();
  const board = valueOf(boardRead);
  const recap = valueOf(useRecap());
  const awards = useAwards();
  const wireRead = useNews('all').snapshot;
  const wire = valueOf(wireRead);
  // The bracket only once it is the season's story: read in the playoffs and
  // after them, and not on every visit before. The week's kind says so where
  // the league's status does not: the practice league's is only ever in
  // season.
  const bracketTime =
    home.league.status === 'playoffs' ||
    home.league.status === 'complete' ||
    home.period.kind === 'playoff' ||
    home.period.kind === 'consolation';
  const bracketRead = useBracket(bracketTime);
  const bracket = valueOf(bracketRead);
  // MY SEASON WRAPPED (H9), asked only once some team's season can be over,
  // and offered first on the page once mine is.
  const wrap = valueOf(useSeasonWrap(bracketTime ? home.league.myTeamId : null));
  // THE YEARBOOK (H9), asked in the same weeks, and offered beside it once
  // the champion is crowned, to everybody in the league.
  const yearbook = valueOf(useYearbook(bracketTime));
  const source = useLeagueSource();
  const installOffer = useInstallOffer(source !== 'sim');
  const { session } = useSession();
  // THE WEEKLY CHALLENGE (Q9): the site's week in the league's sport, and the
  // league's members on its board; nothing to ask in the practice league or
  // for a visitor, who has no account to enter with.
  const challengeSport = source === 'api' && session !== null ? home.league.sport : null;
  const challenge = useChallengeWeek(challengeSport);
  const challengeBoard = useChallengeBoard(challengeSport, home.league.id);
  // THE WEEKS BEFORE, while the card's week has no points yet (B-620): from
  // Thursday night the card's week is the next one, taking picks, and the
  // league's leaders are the week still being played, or, until its first
  // line is read the next morning, the one before it, just over.
  const challengeNow = valueOf(challenge.snapshot);
  const challengeBefore = challengeNow?.weeks.filter((w) => w.period < challengeNow.period) ?? [];
  const challengeLast =
    valueOf(challengeBoard)?.scored === false ? (challengeBefore.at(-1) ?? null) : null;
  const challengeLastRead = useChallengeBoard(
    challengeLast === null ? null : challengeSport,
    home.league.id,
    challengeLast?.season,
    challengeLast?.period,
  );
  const challengeEarlier =
    challengeLast !== null && valueOf(challengeLastRead)?.scored === false
      ? (challengeBefore.at(-2) ?? null)
      : null;
  const challengeEarlierRead = useChallengeBoard(
    challengeEarlier === null ? null : challengeSport,
    home.league.id,
    challengeEarlier?.season,
    challengeEarlier?.period,
  );
  const challengeShown =
    challengeEarlier !== null
      ? { week: challengeEarlier, board: valueOf(challengeEarlierRead) }
      : challengeLast !== null
        ? { week: challengeLast, board: valueOf(challengeLastRead) }
        : null;
  const { account, saveLayout } = useAccountPrefs();
  const [arranging, setArranging] = useState(false);
  const [exporting, setExporting] = useState(false);
  // A STORY'S ADD OR CLAIM (B-416), the wire's and the catch-up's: the sheet
  // a Players row opens.
  const [move, setMove] = useState<RosterMove | null>(null);
  const arrangeable = source !== 'sim' && session !== null && account !== null;
  // A person's look, so a signed-in reader's in a league of their own.
  const catchUp = useCatchUp(
    source !== 'sim' && session !== null ? session.userId : null,
    home.league.id,
  );
  const chat = useChatDrawer();
  const arrangement = arrangeable ? arrangementOf(account.leaguePage) : null;
  const arranged = arrangement !== null && account?.leaguePage !== null;

  // ARRANGED FROM THE PAGE AS IT IS SHOWN (B-408): in the playoffs a page in
  // its usual order has the bracket second, so the list starts there, names
  // it as the page does, and saving it untouched keeps the usual order.
  const shownOrder =
    arrangement !== null && !arranged && bracketTime
      ? { ...arrangement, order: playoffsSecond(arrangement.order, (id) => id) }
      : arrangement;
  if (arranging && shownOrder !== null) {
    return (
      <ArrangeLeaguePage
        initial={shownOrder}
        labels={bracketTime ? { schedule: 'Playoffs' } : undefined}
        onCancel={() => setArranging(false)}
        onDone={(next) => {
          setArranging(false);
          const untouched =
            next.hidden.length === 0 && next.order.join() === shownOrder.order.join() && !arranged;
          void saveLayout({
            leaguePage:
              isUsual(next) || untouched ? null : { order: next.order, hidden: next.hidden },
          });
        }}
      />
    );
  }

  const widgets: Widget[] = [
    ...(installOffer !== null
      ? [{ id: 'install', pinned: true as const, content: installOffer }]
      : []),
    // THE NEXT SEASON once this one is over (H6): a real league's, first on
    // the page, since starting it is the one thing left to do here.
    ...(source === 'api' && home.league.status === 'complete'
      ? [{ id: 'next', column: 1 as const, content: <FrontNextSeason home={home} /> }]
      : []),
    ...(wrap !== null && wrap.over
      ? [{ id: 'wrap', column: 1 as const, content: <FrontWrap wrap={wrap} base={base} /> }]
      : []),
    ...(yearbook !== null && yearbook.over
      ? [
          {
            id: 'yearbook',
            column: 1 as const,
            content: (
              <FrontYearbook book={yearbook} base={base} second={wrap !== null && wrap.over} />
            ),
          },
        ]
      : []),
    ...(catchUp.view !== null && hasNews(catchUp.view)
      ? [
          {
            // FIRST, IN THE PAGE'S FLOW: a pinned section sits over what
            // scrolls, and a long catch-up was cut off on a phone. A page
            // somebody arranged puts it first too (`inOrder`).
            id: 'catchup',
            column: 1 as const,
            content: (
              <CatchUp
                view={catchUp.view}
                zone={zone}
                numbers={standard}
                unread={chat.unread}
                onChat={() => chat.setOpen(true)}
                onDone={catchUp.done}
                onMove={setMove}
              />
            ),
          },
        ]
      : []),
    {
      id: 'headline',
      column: 1,
      content: (
        <Section id="headline" title="The week's story" to={`${base}/recap`} label="the recap">
          <div className="fsp-front-story">
            {recap?.headline ? (
              <>
                <div className="fsp-disp fsp-front-headline">{recap.headline}</div>
                {recap.standfirst ? (
                  <Text tone="secondary" block>
                    {recap.standfirst}
                  </Text>
                ) : null}
              </>
            ) : (
              <Text tone="secondary" block>
                The week's story is written once its games are final.
              </Text>
            )}
            {home.myWeek ? (
              <div className="fsp-front-mine">
                <Text>{home.myWeek.line}</Text>
              </div>
            ) : null}
          </div>
        </Section>
      ),
    },
    {
      id: 'standings',
      column: 1,
      content: (
        <Section id="standings" title="Standings" to={`${base}/standings`} label="the standings">
          {standings === null ? (
            <Waiting read={standingsRead} reading="Reading the table…" />
          ) : (
            <FrontTable view={standings} numbers={standard} league={home} />
          )}
        </Section>
      ),
    },
    {
      id: 'photo',
      column: 1,
      content: (
        <Section id="photo" title="Team photo">
          {standings === null ? (
            <Waiting read={standingsRead} reading="Gathering everybody…" />
          ) : (
            <TeamPhoto
              rows={standings.rows}
              championId={bracket?.champion?.id ?? null}
              base={base}
              league={home.league.name}
              kicker={`${home.period.label} · ${home.league.season}`}
            />
          )}
        </Section>
      ),
    },
    // Beside the team photo, once a week has put a hat on somebody.
    ...(hat !== null && standings !== null
      ? [
          {
            id: 'hat',
            column: 1 as const,
            content: (
              <Section id="hat" title="The hat">
                <HatPanel
                  hat={hat}
                  teams={standings.rows.map((r) => r.team)}
                  myTeamId={home.league.myTeamId}
                  label={`Week ${hat.period}`}
                />
              </Section>
            ),
          },
        ]
      : []),
    {
      id: 'pickem',
      column: 1,
      content: (
        <Section
          id="pickem"
          title="Pick’em"
          to={`${base}/pickem`}
          label="pick’em"
          help={
            <PickemInfo
              lock={(() => {
                const v = valueOf(pickem.snapshot);
                return v === null ? null : lockToName(v);
              })()}
              zone={zone ?? deviceZone()}
            />
          }
        >
          <FrontPickem read={pickem} base={base} zone={zone ?? deviceZone()} numbers={standard} />
        </Section>
      ),
    },
    {
      id: 'challenge',
      column: 1,
      content: (
        <Section
          id="challenge"
          title="Weekly challenge"
          to={`${base}/challenge`}
          label="the weekly challenge"
          help={<ChallengeInfo />}
        >
          <FrontChallenge
            read={challenge.snapshot}
            board={valueOf(challengeBoard)}
            last={challengeShown}
            base={base}
            sim={source === 'sim'}
            signedIn={session !== null}
            zone={zone ?? deviceZone()}
          />
        </Section>
      ),
    },
    // Not in a league that turned suggestions off (RULES_SCHEMA §15c).
    ...(tools.suggestions
      ? [
          {
            // WORTH A LOOK (E1, PLAN-2026-10-27-worth-a-look §6): a tile for the
            // first of each position, at the head of the wire's column.
            id: 'worth',
            column: 3 as const,
            content: (
              <Section
                id="worth"
                title="Worth a look"
                to={`${base}/worth-a-look`}
                label="Worth a look"
                help={<WorthInfo />}
              >
                <FrontWorth read={worth} base={base} numbers={standard} phone={phone} />
              </Section>
            ),
          },
        ]
      : []),
    {
      id: 'week',
      column: 2,
      content: (
        <Section id="week" title="This week" to={`${base}/scoreboard`} label="the scoreboard">
          {board === null ? (
            <Waiting read={boardRead} reading="Reading the week's games…" />
          ) : board.matchups.length === 0 ? (
            <div className="fsp-front-note">
              <Text tone="secondary">No games this week.</Text>
            </div>
          ) : phone ? (
            // ON A PHONE THE GAMES SWIPE (B-405), mine first, each card the
            // scoreboard's own tile with the next one's edge showing.
            <div className="fsp-front-games">
              <CardRow
                label="This week's games"
                what="game"
                slideSize="88%"
                items={[...board.matchups]
                  .sort((a, b) => Number(isMine(b)) - Number(isMine(a)))
                  .map((m) => ({
                    key: m.id,
                    content: <GameTile m={m} awards={awards?.perPeriod} stats={board.stats} />,
                  }))}
              />
            </div>
          ) : (
            <div className="fsp-front-games">
              <GameGrid games={board.matchups} awards={awards?.perPeriod} stats={board.stats} />
            </div>
          )}
        </Section>
      ),
    },
    {
      id: 'wire',
      column: 3,
      content: (
        <Section id="wire" title="The wire" to={`${base}/news?filter=all`} label="the wire">
          {wire === null ? (
            <Waiting read={wireRead} reading="Reading the wire…" />
          ) : wire.length === 0 ? (
            <div className="fsp-front-note">
              <Text tone="secondary">Nothing on the wire yet. News lands as the week goes on.</Text>
            </div>
          ) : (
            <div className="fsp-front-wire">
              {wire.slice(0, 6).map((n) => (
                <NewsRow key={n.id} n={n} zone={zone} onMove={setMove} />
              ))}
              <div className="fsp-front-links">
                <Button to={`${base}/waivers?show=log`} variant="quiet" size="xs" iconRight="next">
                  Every move
                </Button>
              </div>
            </div>
          )}
        </Section>
      ),
    },
    ...(awards === null
      ? []
      : [
          {
            id: 'awards',
            column: 3 as const,
            content: (
              <Expandable id="awards" to={`${base}/awards`} label="the awards">
                {(expand) => <AwardsWidget awards={awards} meta={expand} />}
              </Expandable>
            ),
          },
        ]),
    bracketTime
      ? {
          id: 'schedule',
          column: 1,
          content: (
            <Section
              id="schedule"
              title="Playoffs"
              to={`${base}/schedule?tab=playoffs`}
              label="the playoffs"
            >
              {bracket === null ? (
                <Waiting read={bracketRead} reading="Reading the bracket…" />
              ) : (
                <FrontBracket view={bracket} />
              )}
            </Section>
          ),
        }
      : {
          id: 'schedule',
          column: 2,
          content: (
            <Section
              id="schedule"
              title="Schedule and playoffs"
              to={`${base}/schedule`}
              label="the schedule"
            >
              <div className="fsp-front-story">
                <Text block>{seasonLine(home, standings)}</Text>
                <Line gap={2} wrap>
                  <Button to={`${base}/schedule`} variant="quiet" size="xs" iconRight="next">
                    The weeks ahead
                  </Button>
                  <Button
                    to={`${base}/schedule?tab=playoffs`}
                    variant="quiet"
                    size="xs"
                    iconRight="next"
                  >
                    Playoffs
                  </Button>
                </Line>
              </div>
            </Section>
          ),
        },
    {
      id: 'archive',
      column: 2,
      content: (
        <Section id="archive" title="The archive">
          <Line gap={2} wrap className="fsp-front-links">
            <Button to={`${base}/history`} variant="quiet" size="xs" iconRight="next">
              History and the record book
            </Button>
            {home.league.lastSeasonId !== null ? (
              <Button
                to={`/l/${home.league.lastSeasonId}`}
                variant="quiet"
                size="xs"
                iconRight="next"
              >
                The {home.league.season - 1} season
              </Button>
            ) : null}
            <Button to={`${base}/draft-recap`} variant="quiet" size="xs" iconRight="next">
              Draft recap
            </Button>
            <Button to={`${base}/recap`} variant="quiet" size="xs" iconRight="next">
              Every week's recap
            </Button>
          </Line>
        </Section>
      ),
    },
    {
      id: 'office',
      column: 3,
      content: (
        <Section id="office" title="The office">
          <Line gap={2} wrap className="fsp-front-links">
            <Button to={`${base}/rules`} variant="quiet" size="xs" iconRight="next">
              Rules in plain English
            </Button>
            <Button to={`${base}/managers`} variant="quiet" size="xs" iconRight="next">
              Managers
            </Button>
            <Button to={`${base}/audit`} variant="quiet" size="xs" iconRight="next">
              Commissioner log
            </Button>
            {home.league.isCommissioner ? (
              <Button to={`${base}/settings`} variant="quiet" size="xs" iconRight="next">
                League settings
              </Button>
            ) : null}
            {/* THE LEAGUE AS A FILE (A13, §13.1): any member's, in a real league. */}
            {source === 'api' && !isDemoLeague(home.league.id) ? (
              <Button
                variant="quiet"
                size="xs"
                icon="download"
                loading={exporting}
                onClick={() => {
                  setExporting(true);
                  void exportLeague(home.league.id).then((done) => {
                    setExporting(false);
                    tell(done.ok ? `Saved ${done.name}.` : done.message, done.ok ? 'good' : 'bad');
                  });
                }}
              >
                Export the league
              </Button>
            ) : null}
          </Line>
        </Section>
      ),
    },
  ];

  return (
    <>
      <DashboardScreen
        widgets={
          arranged
            ? inOrder(widgets, arrangement)
            : bracketTime
              ? playoffsSecond(widgets, (w) => w.id)
              : widgets
        }
        ordered={arranged}
        footer={
          arrangeable ? (
            <LeaguePageFoot
              opensOn={account.opensOn[home.league.id] ?? 'team'}
              onOpensOn={(where) => void saveLayout({ opensOn: { [home.league.id]: where } })}
              onArrange={() => setArranging(true)}
            />
          ) : null
        }
      />
      <RosterMoveSheet move={move} showProjection onClose={() => setMove(null)} />
    </>
  );
}

/**
 * Since you last looked (K7, B-411): my results, the others settled, the
 * league's moves and my players on the report since my last look, and chat's
 * unread; Got it puts it away. Simple keeps the scores out of my results.
 */
function CatchUp({
  view,
  zone,
  numbers,
  unread,
  onChat,
  onDone,
  onMove,
}: {
  view: CatchUpView;
  zone: string | null;
  numbers: boolean;
  unread: number;
  onChat: () => void;
  onDone: () => void;
  /** A story's Add or Claim (B-416). */
  onMove: (move: RosterMove) => void;
}) {
  const base = useLeagueBase();
  // From when: the look, or the fortnight a long absence is held to.
  const clamped = view.from !== null && view.since !== null && view.from !== view.since;
  const when =
    view.from === null || zone === null
      ? null
      : clamped
        ? `The last two weeks, since ${whenLabel(view.from, zone)}`
        : `Since ${whenLabel(view.from, zone)}`;
  const score = (a: number, b: number) => (numbers ? ` ${a.toFixed(1)}–${b.toFixed(1)}` : '');
  const more = view.moveCount - view.moves.length;
  return (
    <div className="fsp-front-section">
      <Panel
        title="Since you last looked"
        meta={
          <Button variant="quiet" size="xs" onClick={onDone}>
            Got it
          </Button>
        }
      >
        <div className="fsp-front-catchup">
          {when !== null ? (
            <Text tone="secondary" block>
              {when}
            </Text>
          ) : null}
          {view.results.map((r) => (
            <Line key={r.matchupId} gap={2} wrap>
              <Text block>
                {r.outcome === 'won'
                  ? `You beat ${r.opponent.name}${score(r.myScore, r.theirScore)} in ${r.label}.`
                  : r.outcome === 'lost'
                    ? `${r.opponent.name} beat you${score(r.theirScore, r.myScore)} in ${r.label}.`
                    : `You tied ${r.opponent.name}${score(r.myScore, r.theirScore)} in ${r.label}.`}
              </Text>
              <Button
                to={`${base}/matchup/${r.matchupId}`}
                variant="quiet"
                size="xs"
                iconRight="next"
              >
                The matchup
              </Button>
            </Line>
          ))}
          {view.otherGames > 0 ? (
            <Line gap={2} wrap>
              <Text block>
                {view.otherGames === 1
                  ? '1 other game went final.'
                  : `${view.otherGames} other games went final.`}
              </Text>
              <Button to={`${base}/scoreboard`} variant="quiet" size="xs" iconRight="next">
                Scoreboard
              </Button>
            </Line>
          ) : null}
          {view.moves.length > 0 ? (
            <div className="fsp-front-wire">
              <Caption>
                {view.moveCount === 1 ? '1 player move' : `${view.moveCount} player moves`}
              </Caption>
              {view.moves.slice(0, 5).map((n) => (
                <NewsRow key={n.id} n={n} zone={zone} onMove={onMove} />
              ))}
              {more > 0 || view.moves.length > 5 ? (
                <div className="fsp-front-links">
                  <Button
                    to={`${base}/news?filter=moves`}
                    variant="quiet"
                    size="xs"
                    iconRight="next"
                  >
                    Every move
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
          {view.news.length > 0 ? (
            <div className="fsp-front-wire">
              <Caption>Your players on the injury report</Caption>
              {view.news.map((n) => (
                <NewsRow key={n.id} n={n} zone={zone} onMove={onMove} />
              ))}
            </div>
          ) : null}
          {unread > 0 ? (
            <div className="fsp-front-links">
              <Button variant="quiet" size="xs" icon="chat" onClick={onChat}>
                {unread === 1 ? '1 unread in chat' : `${unread} unread in chat`}
              </Button>
            </div>
          ) : null}
        </div>
      </Panel>
    </div>
  );
}

/** In the playoffs the bracket follows the week's story (B-408), in a page's usual order. */
function playoffsSecond<T>(items: readonly T[], idOf: (item: T) => string): T[] {
  const bracket = items.find((t) => idOf(t) === 'schedule');
  if (bracket === undefined || !items.some((t) => idOf(t) === 'headline')) return [...items];
  const rest = items.filter((t) => t !== bracket);
  rest.splice(rest.findIndex((t) => idOf(t) === 'headline') + 1, 0, bracket);
  return rest;
}

/**
 * YOUR SEASON (H9, PLAN-2026-10-18 §12.1), once mine is over: the finish in a
 * line and the way into the season told as cards. It comes and goes on its
 * own, as the catch-up does, so it is not one of the regions a person moves.
 */
function FrontWrap({ wrap, base }: { wrap: SeasonWrapView; base: string }) {
  const finish = wrap.cards.find((c) => c.id === 'finish');
  return (
    <div className="fsp-front-section">
      <Panel title="Your season">
        <div className="fsp-front-wrap">
          {finish ? (
            <Line gap={3} align="start">
              {finish.value ? (
                <span className="fsp-disp fsp-front-wrap-value">{finish.value}</span>
              ) : null}
              <Text>{finish.line}</Text>
            </Line>
          ) : null}
          <div>
            <Button variant="primary" size="sm" icon="trophy" to={`${base}/wrapped`}>
              Open your season
            </Button>
          </div>
        </div>
      </Panel>
    </div>
  );
}

/**
 * THE YEARBOOK (H9, PLAN-2026-10-31-the-yearbook §4), once the champion is
 * crowned: the champion's line and the way in. Its button steps back to a
 * second one under *Your season*, so the page leads with one.
 */
function FrontYearbook({
  book,
  base,
  second,
}: {
  book: YearbookView;
  base: string;
  second: boolean;
}) {
  return (
    <div className="fsp-front-section">
      <Panel title="The yearbook">
        <div className="fsp-front-wrap">
          {book.champion ? <Text>{book.champion.line}</Text> : null}
          <div>
            <Button
              variant={second ? 'secondary' : 'primary'}
              size="sm"
              icon="yearbook"
              to={`${base}/yearbook`}
            >
              Open the yearbook
            </Button>
          </div>
        </div>
      </Panel>
    </div>
  );
}

/**
 * PICK'EM ON THE FRONT (I6, B-447, B-484): this week's card — how many games,
 * when it closes, how many of them my team has picked — and the table's top
 * three once a pick has scored, with the way in. Each line its own, spaced, and
 * the leaders under a name of their own: run together, they read as one
 * sentence and a list of zeros (2026-10-23). The practice league says it has
 * none yet.
 */
function FrontPickem({
  read,
  base,
  zone,
  numbers,
}: {
  read: ReturnType<typeof usePickem>;
  base: string;
  zone: string;
  /** Standard and up: the counts, the meter and the points. Simple keeps the sentences without them. */
  numbers: boolean;
}) {
  if (read.source === 'sim') {
    return (
      <div className="fsp-front-note">
        {/* TODO(I6): pick'em in the practice league's simulator. */}
        <Text tone="secondary">
          Pick’em plays in real leagues: each week every team calls the league’s games.
        </Text>
      </div>
    );
  }
  const view = valueOf(read.snapshot);
  if (view === null) return <Waiting read={read.snapshot} reading="Reading pick’em…" />;
  const games = view.matchups.length;
  const mine = view.matchups.filter((m) => m.myPick !== null).length;
  const line =
    games === 0
      ? `Nothing to pick in ${view.period.label}.`
      : view.locked
        ? numbers
          ? `${view.period.label} is locked: ${games} ${games === 1 ? 'game' : 'games'}.`
          : `${view.period.label} is locked.`
        : `${view.period.label}${numbers ? ` · ${games} ${games === 1 ? 'game' : 'games'}` : ''}${
            view.locksAt !== null ? ` · closes ${fmtWhen(view.locksAt, zone)}` : ''
          }`;
  // HOW FAR MY PICKS HAVE GOT: the same sentence at every level where it has
  // no number in it (none yet, all in), the count and its meter at Standard.
  const picked = !view.canPick
    ? null
    : mine === games
      ? 'Your picks are in'
      : mine === 0
        ? 'You haven’t picked yet'
        : numbers
          ? `You’ve picked ${mine} of ${games}`
          : null;
  // AND THE BULLSEYE'S GUESS (PLAN-2026-10-30-bullseye §5), while it is open
  // and my team has a game to score in: a bye has nothing to guess.
  const plays = view.matchups.some((m) => m.home.isMine || m.away.isMine);
  const progress =
    picked === null
      ? null
      : `${picked}${plays && view.myGuess === null ? ', and you can guess your score.' : '.'}`;
  const scored = view.table.some((r) => r.decided > 0);
  return (
    <div className="fsp-front-note fsp-front-pickem">
      <Stack gap={3}>
        <Stack gap={1}>
          <Text weight="strong" block>
            {line}
          </Text>
          {progress !== null ? (
            <Text size="sm" tone="secondary" block>
              {progress}
            </Text>
          ) : null}
          {/* Empty, a meter reads as a rule under the line: it waits for a pick. */}
          {numbers && view.canPick && mine > 0 && mine < games ? (
            <Meter value={mine / games} label={`${mine} of ${games} picked`} />
          ) : null}
        </Stack>
        {scored ? (
          <Stack gap={1}>
            <Caption>Leaders</Caption>
            <ol className="fsp-front-pickem-leaders">
              {view.table.slice(0, 3).map((r, i) => (
                <li key={r.team.id} data-mine={r.team.isMine || undefined}>
                  <span className="fsp-front-pickem-rank fsp-num">{i + 1}</span>
                  <TeamMark team={r.team} size={24} />
                  <span className="fsp-front-pickem-name">{r.team.name}</span>
                  {numbers ? (
                    <span className="fsp-num fsp-front-pickem-points">
                      {Number.isInteger(r.points) ? r.points : r.points.toFixed(1)}
                    </span>
                  ) : null}
                </li>
              ))}
            </ol>
          </Stack>
        ) : games > 0 ? (
          <Text size="sm" tone="secondary" block>
            No points yet: they come in as each week’s games finish.
          </Text>
        ) : null}
        {games > 0 ? (
          <div>
            <Button
              size="xs"
              variant={view.canPick && mine < games ? 'primary' : 'secondary'}
              to={`${base}/pickem`}
            >
              {view.canPick
                ? mine === games
                  ? 'Change your picks'
                  : 'Make your picks'
                : 'See the picks'}
            </Button>
          </div>
        ) : null}
      </Stack>
    </div>
  );
}

/**
 * THE WEEKLY CHALLENGE ON THE FRONT (Q9, PLAN-2026-11-01-the-weekly-challenge
 * §7): the week and what it plays, where my entry stands and my place among
 * everybody, the league's top three once a pick has scored, and the way in.
 * The practice league and a visitor say why it is not theirs here.
 */
function FrontChallenge({
  read,
  board,
  last,
  base,
  sim,
  signedIn,
  zone,
}: {
  read: Snapshot<ChallengeWeekViewShape | null>;
  board: ChallengeBoardViewShape | null;
  /** The week before, while this one has no points yet: its league board. */
  last: {
    week: ChallengeWeekViewShape['weeks'][number];
    board: ChallengeBoardViewShape | null;
  } | null;
  base: string;
  sim: boolean;
  signedIn: boolean;
  zone: string;
}) {
  if (sim || !signedIn)
    return (
      <div className="fsp-front-note">
        {/* TODO(Q9): the weekly challenge in the practice league's simulator. */}
        <Text tone="secondary">
          {sim
            ? 'The weekly challenge plays the real NFL week, the same for everybody on the site, so it is in your real leagues.'
            : 'Everybody on the site plays the same week. Sign in to make your picks.'}
        </Text>
      </div>
    );
  const week = valueOf(read);
  if (week === null) return <Waiting read={read} reading="Reading the weekly challenge…" />;
  const plays = week.positions.map((p) => plural(p.name).toLowerCase()).join(' and ');
  const slotList = slotsOf(week);
  const slots = slotList.length;
  const picks = week.mine?.picks ?? slotList.map(() => null);
  const picked = picks.filter((p) => p !== null).length;
  // A PIP A PICK once the week is open, or on an entry made: they say how
  // many are picked, so the words do not say it again.
  const pips = week.positions.length > 0 && (week.state === 'open' || week.mine !== null);
  const line =
    week.state === 'upcoming'
      ? `${week.label}’s challenge opens ${fmtWhen(week.opensAt, zone)}.`
      : `${week.label}: ${plays}.`;
  const where =
    week.state === 'upcoming'
      ? null
      : week.state === 'open'
        ? picked === 0
          ? 'You haven’t entered yet. Each pick locks when his game starts.'
          : picked === slots
            ? 'Your entry is in.'
            : null
        : week.state === 'playing'
          ? 'Every game has started.'
          : 'The week is over.';
  const place =
    week.mine !== null && week.mine.place !== null
      ? `You’re ${ordinal(week.mine.place)} of ${week.entries} with ${tenths(week.mine.points)}.`
      : null;
  // The league's top three once a pick has scored: before, every entry has
  // nothing, and the week before's are shown, as it is played or once over.
  const shown = board?.scored
    ? { board, label: null }
    : last?.board?.scored
      ? {
          board: last.board,
          label: `${last.week.label}${last.week.state === 'over' ? '' : ' so far'}`,
        }
      : null;
  const leaders = shown === null ? [] : shown.board.rows.slice(0, 3);
  const lastMe =
    shown?.label != null && shown.board.me !== null
      ? `On everybody’s board you’re ${ordinal(shown.board.me.place)} of ${shown.board.entries}, with ${tenths(shown.board.me.points)}.`
      : null;
  return (
    <div className="fsp-front-note fsp-front-challenge">
      <Stack gap={3}>
        {/* THE WEEK'S BANNER, small (B-619): its two positions' colors. */}
        <ChallengeBanner positions={week.positions.map((p) => p.position)} compact>
          <Stack gap={1}>
            <Text weight="strong" block>
              {line}
            </Text>
            {where !== null ? (
              <Text size="sm" tone="secondary" block>
                {where}
              </Text>
            ) : null}
            {pips ? (
              <ChallengePips
                slots={slotList.map((s) => s.position.position)}
                picks={picks}
                captain={week.mine?.captain ?? null}
              />
            ) : null}
            {place !== null ? (
              <Text size="sm" weight="strong" block>
                {place}
              </Text>
            ) : null}
          </Stack>
        </ChallengeBanner>
        {leaders.length > 0 ? (
          <Stack gap={1}>
            <Caption>Your league{shown?.label != null ? ` · ${shown.label}` : ''}</Caption>
            {lastMe !== null ? (
              <Text size="sm" block>
                {lastMe}
              </Text>
            ) : null}
            <ol className="fsp-front-challenge-leaders">
              {leaders.map((r) => (
                <li key={r.team?.id ?? r.name} data-mine={r.mine || undefined}>
                  <span className="fsp-num fsp-challenge-place">
                    <Medal n={r.place} />
                  </span>
                  {r.team !== null ? <TeamMark team={r.team} size={24} /> : null}
                  <span className="fsp-challenge-board-name">{r.name}</span>
                  <span className="fsp-num">{tenths(r.points)}</span>
                </li>
              ))}
            </ol>
          </Stack>
        ) : null}
        <div>
          <Button
            size="xs"
            variant={week.state === 'open' && picked < slots ? 'primary' : 'secondary'}
            to={`${base}/challenge`}
          >
            {week.state === 'open'
              ? picked === 0
                ? 'Make your picks'
                : 'Change your picks'
              : 'See the week'}
          </Button>
        </div>
      </Stack>
    </div>
  );
}

/**
 * A section's read on its way, or why it did not come: a read that failed
 * says so rather than "Reading…" for good (the review of B-408).
 */
function Waiting({ read, reading }: { read: Snapshot<unknown>; reading: string }) {
  const failed = failureOf(read);
  return (
    <div className="fsp-front-note">
      <Text tone="secondary">{failed === null ? reading : failed.message}</Text>
    </div>
  );
}

/**
 * The playoffs on the front page: the bracket's headline and the round under
 * way, or the next to be played, or with every game done the last one —
 * each game opening its matchup, as the bracket's own page does.
 */
function FrontBracket({ view }: { view: BracketView }) {
  const navigate = useNavigate();
  const base = useLeagueBase();
  const round =
    view.rounds.find((r) => r.period.isCurrent) ??
    view.rounds.find((r) => r.games.some((g) => g.status !== 'final' && g.status !== 'bye')) ??
    view.rounds[view.rounds.length - 1];
  const open = (g: BracketGame) =>
    g.matchupId ? navigate(`${base}/matchup/${g.matchupId}`) : undefined;
  return (
    <div className="fsp-front-story fsp-front-bracket">
      <Text weight="bold" block>
        {view.headline}
      </Text>
      {round === undefined ? (
        <Text tone="secondary" block>
          No playoffs in this league. The season decides it.
        </Text>
      ) : (
        <BracketColumn round={round} onOpen={open} />
      )}
    </div>
  );
}

/** A game of mine leads the row. */
function isMine(m: {
  home: { team: { isMine: boolean } };
  away: { team: { isMine: boolean } };
}): boolean {
  return m.home.team.isMine || m.away.team.isMine;
}

/** Where the season is, in a sentence: the week, and when the playoffs come. */
function seasonLine(home: HomeView, standings: StandingsView | null): string {
  const week = home.period.label;
  const end = standings?.regularSeasonEnd ?? null;
  const field = standings?.playoffTeamCount ?? null;
  if (end === null) return `${week}.`;
  return field !== null && field > 0
    ? `${week}. The regular season ends after Week ${end}, and ${field} teams make the playoffs.`
    : `${week}. The regular season ends after Week ${end}.`;
}

/**
 * The table, as the front page sets it: every team, the playoff line after
 * the last seed, Clinched and Out in words. The record, or how far back in a
 * race on points; points for from Standard.
 */
function FrontTable({
  view,
  numbers,
  league,
}: {
  view: StandingsView;
  numbers: boolean;
  league: HomeView;
}) {
  const race = racesOnPoints(league.league.format.id);
  // THE LINE ONLY WHERE THE FIELD IS THE TOP OF THE TABLE, as the standings
  // draw it: a division winner below the cut is in, and a line would say not.
  const cut = view.playoffTeamCount > 0 && view.rows.length > view.playoffTeamCount;
  const line = cut && fieldIsTheTop(view.rows, view.playoffTeamCount);
  return (
    <div className="fsp-front-table" role="table" aria-label="Standings">
      <div className="fsp-front-row fsp-front-head" role="row">
        <span role="columnheader" className="fsp-cap">
          #
        </span>
        <span role="columnheader" className="fsp-cap">
          Team
        </span>
        <span role="columnheader" className="fsp-cap fsp-right">
          {race ? (
            <Term tip={GLOSSARY.pointsBack}>Back</Term>
          ) : (
            <Term tip={GLOSSARY.winLoss}>W–L</Term>
          )}
        </span>
        {numbers ? (
          <span role="columnheader" className="fsp-cap fsp-right">
            <Term tip={GLOSSARY.pointsFor}>PF</Term>
          </span>
        ) : null}
      </div>
      {view.rows.map((r, i) => (
        <div
          key={r.team.id}
          role="row"
          className={`fsp-front-row${r.team.isMine ? ' fsp-row-selected' : ''}`}
          // THE PLAYOFF LINE, under the last team in the field.
          data-line={line && i === view.playoffTeamCount - 1 ? '' : undefined}
        >
          <span role="cell" className="fsp-num fsp-muted">
            {r.rank ?? '—'}
          </span>
          <span role="cell" className="fsp-front-team">
            <TeamMark team={r.team} size={22} />
            <TeamName team={r.team} className="fsp-front-name" />
            <RaceMark status={r.playoffStatus} />
          </span>
          <span role="cell" className="fsp-num fsp-right">
            {race
              ? r.pointsBack === null || r.pointsBack === 0
                ? '—'
                : r.pointsBack.toFixed(1)
              : `${r.wins}–${r.losses}${r.ties ? `–${r.ties}` : ''}`}
          </span>
          {numbers ? (
            <span role="cell" className="fsp-num fsp-right">
              {r.pointsFor.toFixed(1)}
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}
